import { mapActions, mapState } from 'vuex';
import selectBox from '../../../../common/components/select-box/select-box.vue';
import complaintsFileUpload from './complaints-file-upload/complaints-file-upload.vue';
import { VueAccordion, VueAccordionItem } from '@ui-common/vue-accordion';
import { required, maxLength } from 'vuelidate/lib/validators';
import { SCROLL_TO_OFFSET, ERROR_CLASS, ROUTE_NAMES } from '../../constants/complaint-constants';
import { VueSelect, VueSelectOption } from '@ui-common/vue-select';
import VueErrorMessage from '@ui-common/vue-error-message';
import VueTextarea from '@ui-common/vue-textarea';
import VueCaptcha from '@ui-common/vue-captcha';
import VueCheckbox from '@ui-common/vue-checkbox';
import VueRadioGroup from '@ui-common/vue-radio-group';
import { jumpToFirstError } from '../../../../common/js/find-first-error-component/find-first-error-component';
import messages from './complaints-problem-form-lang.json';
const DOT = '.';
const COMMA = ',';
const OR = 'vagy';
const FILE_EXTENSION_ERROR = 'ERR-INVALID-FILE-EXTENSION';
const FILE_SIZE_ERROR = 'ERR-MAX-FILE-SIZE-EXCEEDED';
const MAX_FILE_SIZE_MULTIPLIER = 1000000;

export default {
	mixins: [jumpToFirstError],
	i18n: { messages },
	data() {
		return {
			model: {
				topic: null,
				subTopic: null,
				description: '',
				disclaimerAccepted: false,
				captcha: {
					captchaText: ''
				}
			},
			loadedFile: null,
			showUploadedFileExtensionError: false,
			showUploadedFileSizeError: false,
			showCaptchaError: false,
			fileUploadOpened: false,
			showUploadedFilesAmountError: false
		};
	},
	props: {
		privacyPolicyUrl: {
			required: false
		},
		loading: {
			required: true
		},
		topics: {
			required: true,
			type: Array
		},
		captcha: {
			required: true
		},
		descriptionMaxLength: {
			required: true
		},
		fileUploadEnabled: {
			required: true
		},
		enabledFileExtensions: {
			required: true
		},
		maxFileSize: {
			required: true
		},
		maxFilesAmount: {
			required: true
		},
		captchaBackendError: {
			required: false
		},
		problemData: {
			required: true
		},
		captchaValidated: {
			required: true
		}
	},
	validations() {
		let model = {};
		model.topic = { required };
		model.subTopic = this.currentSubtopics.length && this.model.topic && this.currentSubtopicLabel ? { required } : {};
		model.description = {
			required,
			maxLength: maxLength(this.descriptionMaxLength)
		};
		model.disclaimerAccepted = { checked: (value) => value === true };
		model.captcha = {};
		model.captcha.captchaText = this.captcha && this.captcha.htmlFragment && !this.captchaValidated ? { required } : {};
		return { model };
	},
	components: {
		selectBox,
		complaintsFileUpload,
		VueAccordion,
		VueAccordionItem,
		VueSelect,
		VueSelectOption,
		VueErrorMessage,
		VueTextarea,
		VueCaptcha,
		VueCheckbox,
		VueRadioGroup
	},
	methods: {
		...mapActions('complaints', ['goToNextStep', 'submitProblemForm', 'uploadFile']),
		isFormInvalid() {
			this.$v.model.$touch();
			return this.$v.$invalid;
		},
		postForm() {
			if (this.currentSubtopics.length == 1) {
				this.model.subTopic = this.currentSubtopics[0].id;
				return this.submitProblemForm({ lang: this.$i18n.locale, model: this.model, currentSubTopic: this.currentSubtopics[0].id });
			} else {
				return this.submitProblemForm({ lang: this.$i18n.locale, model: this.model, currentSubTopic: this.currentSubTopic });
			}
		},
		scrollToErrorMessage() {
			this.$nextTick(() => {
				let elem = this.$el.querySelector(ERROR_CLASS);
				if (elem) {
					this.$scrollTo(elem, { offset: SCROLL_TO_OFFSET });
				}
			});
		},
		nextStep() {
			this.$router.push(ROUTE_NAMES.PERSONAL);
		},
		resetSubtopic() {
			this.model.subTopic = null;
			this.$v.model.subTopic.$reset();
		},
		onFileLoaded(file) {
			if (this.filesMaxAmountLimitReached) {
				this.showUploadedFilesAmountError = true;
			} else {
				this.showUploadedFilesAmountError = false;
				this.showUploadedFileExtensionError = false;
				this.showUploadedFileSizeError = false;
				this.loadedFile = null;
				let extension = DOT + file.name.split(DOT).pop().toLowerCase();
				if (this.enabledFileExtensions.indexOf(extension) == -1) {
					this.showUploadedFileExtensionError = true;
					this.loadedFile = this.createFileErrorObject(file, FILE_EXTENSION_ERROR);
					this.$nextTick(() => {
						this.$refs.fileUploadErrorMessage.focus();
					});
				}
				if (file.size > this.maxFileSize * MAX_FILE_SIZE_MULTIPLIER) {
					this.showUploadedFileSizeError = true;
					this.loadedFile = this.createFileErrorObject(file, FILE_SIZE_ERROR);
					this.$nextTick(() => {
						this.$refs.fileUploadErrorMessage.focus();
					});
				}
				if (!this.showUploadedFileSizeError && !this.showUploadedFileExtensionError) {
					this.$emit('on-file-upload', file);
				}
			}
		},
		createFileErrorObject(file, errorCode) {
			return {
				filename: file.name,
				path: null,
				id: null,
				errorCode
			};
		},
		deleteItem(fileId) {
			if (this.filesMaxAmountLimitReached) {
				this.showUploadedFilesAmountError = false;
			}
			this.$emit('on-delete-file', fileId);
		}
	},
	computed: {
		...mapState('complaints', ['filesUploaded', 'uploadErrorCode', 'initData']),
		ACCEPTED_FILE_FORMATS() {
			return this.enabledFileExtensions.toString();
		},
		ACCEPTED_FILE_FORMATS_TEXT() {
			return this.enabledFileExtensions
				.map((extension, index) => {
					if (index == this.enabledFileExtensions.length - 2) {
						return extension + ' ' + OR + ' ';
					} else if (index == this.enabledFileExtensions.length - 1) {
						return extension;
					} else {
						return extension + COMMA + ' ';
					}
				})
				.join('');
		},
		currentTopic() {
			return this.topics.find((item) => {
				return item.id == this.model.topic;
			});
		},
		currentSubTopic() {
			return this.currentSubtopics.find((item) => {
				return item.id == this.model.subTopic;
			});
		},
		currentSubtopics() {
			return this.currentTopic && this.currentTopic.subTopics ? this.currentTopic.subTopics : [];
		},
		currentSubtopicLabel() {
			return this.currentTopic && this.currentTopic.subTopicLabel ? this.currentTopic.subTopicLabel : '';
		},
		currentSubTopicHint() {
			return this.currentSubTopic && this.currentSubTopic.hint ? this.currentSubTopic.hint : '';
		},
		allFilesList() {
			let result = [...this.filesUploaded];
			if (this.loadedFile && !this.loadedFile.errorCode) {
				result.push(this.loadedFile);
			}
			return result;
		},
		filesMaxAmountLimitReached() {
			let resultArray = this.filesUploaded.filter((item) => !item.errorCode);
			return resultArray.length == this.maxFilesAmount;
		},
		descriptionLength() {
			return this.model.description.length;
		},
		subtopicOptions() {
			return this.currentSubtopics.map((topic) => {
				return {
					key: topic.id,
					value: topic.name
				};
			});
		}
	},
	watch: {
		captchaBackendError(newValue) {
			if (newValue) {
				this.$v.model.captcha.captchaText.$reset();
				this.model.captcha.captchaText = '';
				this.showCaptchaError = true;
			}
		},
		uploadErrorCode(newValue) {
			if (newValue) {
				if (newValue == FILE_EXTENSION_ERROR) {
					this.showUploadedFileExtensionError = true;
				} else if (newValue == FILE_SIZE_ERROR) {
					this.showUploadedFileSizeError = true;
				}
			}
		}
	},
	created() {
		if (this.problemData) {
			this.model = JSON.parse(JSON.stringify(this.problemData));
			this.model.captcha.captchaText = '';
		}
	}
};
